import React, { useEffect, useState, useMemo } from 'react';
import { Box, Paper, Typography, Grid, Button, TextField, Snackbar, Alert, Table, TableHead, TableRow, TableCell, TableBody, CircularProgress, MenuItem, Card, CardContent, Dialog, DialogTitle, DialogContent, DialogActions, IconButton } from '@mui/material';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Legend } from 'recharts';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';
import SavedFeaturesDnDTable from './SavedFeaturesDnDTable';
import NewFeaturesDnDTable from './NewFeaturesDnDTable';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';

const emptyFeature = { project_id: '', fr_id: '', name: '', feature: '', description: '' };
const emptyTask = { project_id: '', feature_id: '', title: '', description: '', status: '', assignee: '' };
const emptyProject = { name: '', description: '' };
const TASK_STATUSES = ['', 'todo', 'in progress', 'done', 'blocked'];

const FeatureTaskPage = () => {
  const [features, setFeatures] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [projects, setProjects] = useState([]);
  const [featureForm, setFeatureForm] = useState(emptyFeature);
  const [taskForm, setTaskForm] = useState(emptyTask);
  const [projectForm, setProjectForm] = useState(emptyProject);
  const [editingFeatureId, setEditingFeatureId] = useState(null);
  const [editingTaskId, setEditingTaskId] = useState(null);
  const [featureSuccess, setFeatureSuccess] = useState('');
  const [featureError, setFeatureError] = useState('');
  const [taskSuccess, setTaskSuccess] = useState('');
  const [taskError, setTaskError] = useState('');
  const [projectSuccess, setProjectSuccess] = useState('');
  const [projectError, setProjectError] = useState('');
  const [loading, setLoading] = useState(true);
  const [featureFilters, setFeatureFilters] = useState({ q: '', project_id: '' });
  const [taskFilters, setTaskFilters] = useState({ q: '', status: '', assignee: '' });
  const [featureDialogOpen, setFeatureDialogOpen] = useState(false);
  const [taskDialogOpen, setTaskDialogOpen] = useState(false);
  const [projectDialogOpen, setProjectDialogOpen] = useState(false);
  const [featureDialogFor, setFeatureDialogFor] = useState('feature'); // 'feature' or 'task'
  const [selectedProjectId, setSelectedProjectId] = useState('');
  const [newFeatures, setNewFeatures] = useState([]);
  // Add state for reordered features
  const [reorderedFeatures, setReorderedFeatures] = useState([]);
  const [isReordering, setIsReordering] = useState(false);
  const [projectSearch, setProjectSearch] = useState("");
  const [featureSearch, setFeatureSearch] = useState("");

  // Filtered projects for search
  const filteredProjects = useMemo(() => {
    if (!projectSearch.trim()) return projects;
    const q = projectSearch.trim().toLowerCase();
    return projects.filter(
      p =>
        (p.name && p.name.toLowerCase().includes(q)) ||
        (p.description && p.description.toLowerCase().includes(q))
    );
  }, [projects, projectSearch]);

  // Filtered features for search
  const filteredFeaturesWithFrId = useMemo(() => {
    if (!featureSearch.trim()) return reorderedFeatures;
    const q = featureSearch.trim().toLowerCase();
    return reorderedFeatures.filter(
      f =>
        (f.fr_id && f.fr_id.toLowerCase().includes(q)) ||
        (f.name && f.name.toLowerCase().includes(q)) ||
        (f.feature && f.feature.toLowerCase().includes(q)) ||
        (f.description && f.description.toLowerCase().includes(q))
    );
  }, [reorderedFeatures, featureSearch]);

  // Fetch projects
  const fetchProjects = () => {
    fetch('/api/projects')
      .then(res => res.json())
      .then(data => setProjects(data));
  };

  // Fetch features
  const fetchFeatures = () => {
    setLoading(true);
    let url = '/api/features?';
    Object.entries(featureFilters).forEach(([k, v]) => { if (v) url += `${k}=${v}&`; });
    fetch(url)
      .then(res => res.json())
      .then(data => { setFeatures(data); setLoading(false); });
  };

  // Fetch tasks
  const fetchTasks = () => {
    setLoading(true);
    let url = '/api/tasks?';
    Object.entries(taskFilters).forEach(([k, v]) => { if (v) url += `${k}=${v}&`; });
    fetch(url)
      .then(res => res.json())
      .then(data => { setTasks(data); setLoading(false); });
  };

  useEffect(() => { fetchProjects(); }, []);
  useEffect(() => { fetchFeatures(); }, [featureFilters]);
  useEffect(() => { fetchTasks(); }, [taskFilters]);

  // Generate mock FR_IDs for features of the selected project
  const featuresWithFrId = useMemo(() => {
    const filtered = features.filter(f => f.project_id === selectedProjectId);
    return filtered.map((f, idx) => ({
      ...f,
      fr_id: f.fr_id || `FR${String(idx + 1).padStart(4, '0')}`
    }));
  }, [features, selectedProjectId]);

  // When featuresWithFrId changes, update reorderedFeatures
  useEffect(() => {
    setReorderedFeatures(featuresWithFrId);
  }, [featuresWithFrId]);

  // Handle drag end for saved features
  const handleSavedFeaturesDragEnd = result => {
    if (!result.destination) return;
    const reordered = Array.from(reorderedFeatures);
    const [removed] = reordered.splice(result.source.index, 1);
    reordered.splice(result.destination.index, 0, removed);
    setReorderedFeatures(reordered);
    setIsReordering(true);
  };

  // Save reordered features (update FR_IDs)
  const handleSaveFeatureOrder = async () => {
    if (!selectedProjectId) return;
    try {
      const payload = reorderedFeatures.map((f, idx) => ({
        id: f.id,
        fr_id: `FR${String(idx + 1).padStart(4, '0')}`
      }));
      const res = await fetch('/api/features/reorder', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ project_id: selectedProjectId, order: payload })
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to save order');
      setFeatureSuccess('Feature order saved!');
      setIsReordering(false);
      fetchFeatures();
    } catch (err) {
      setFeatureError(err.message);
    }
  };

  // Chart data
  const taskStatusData = useMemo(() => {
    const counts = TASK_STATUSES.filter(Boolean).map(status => ({
      status,
      count: tasks.filter(t => t.status === status).length
    }));
    return counts;
  }, [tasks]);

  // Helper to generate next FR_ID for new rows (frontend only, backend will enforce uniqueness)
  const getNextFrId = () => {
    const existingFrIds = features
      .filter(f => f.project_id === selectedProjectId)
      .map(f => f.fr_id)
      .concat(newFeatures.map(f => f.fr_id));
    let nextNum = 1;
    while (existingFrIds.includes(`FR${String(nextNum).padStart(4, '0')}`)) {
      nextNum++;
    }
    return `FR${String(nextNum).padStart(4, '0')}`;
  };

  // Add new feature row
  const handleAddNewFeatureRow = () => {
    setNewFeatures(arr => [
      ...arr,
      {
        fr_id: getNextFrId(),
        name: '',
        feature: '',
        description: '',
        project_id: selectedProjectId
      }
    ]);
  };

  // Remove new feature row
  const handleRemoveNewFeatureRow = idx => {
    setNewFeatures(arr => arr.filter((_, i) => i !== idx));
  };

  // Update new feature row
  const handleNewFeatureChange = (idx, field, value) => {
    setNewFeatures(arr => arr.map((f, i) => i === idx ? { ...f, [field]: value } : f));
  };

  // Handle drag end for new features
  const handleNewFeaturesDragEnd = result => {
    if (!result.destination) return;
    const reordered = Array.from(newFeatures);
    const [removed] = reordered.splice(result.source.index, 1);
    reordered.splice(result.destination.index, 0, removed);
    setNewFeatures(reordered);
  };

  // Save all new features
  const handleSaveAllNewFeatures = async () => {
    if (!selectedProjectId) return;
    // Validate all rows
    for (const f of newFeatures) {
      if (!f.name || !f.feature) {
        setFeatureError('Module and Feature are required for all new features.');
        return;
      }
    }
    setFeatureError('');
    setFeatureSuccess('');
    try {
      const res = await fetch('/api/features/bulk-json', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(newFeatures.map(f => ({
          ...f,
          project_id: selectedProjectId
        })))
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Bulk add failed');
      setFeatureSuccess(`Added ${data.success} features.`);
      setNewFeatures([]);
      fetchFeatures();
    } catch (err) {
      setFeatureError(err.message);
    }
  };

  // Feature form handlers
  const handleFeatureChange = e => setFeatureForm(f => ({ ...f, [e.target.name]: e.target.value }));
  const handleFeatureSubmit = async e => {
    e.preventDefault();
    setFeatureError(''); setFeatureSuccess('');
    try {
      // For now, send fr_id to backend, but backend will ignore it
      const res = await fetch(editingFeatureId ? `/api/features/${editingFeatureId}` : '/api/features', {
        method: editingFeatureId ? 'PUT' : 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(featureForm)
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to save feature');
      setFeatureSuccess(editingFeatureId ? 'Feature updated!' : 'Feature added!');
      setFeatureForm(emptyFeature);
      setEditingFeatureId(null);
      setFeatureDialogOpen(false);
      fetchFeatures();
    } catch (err) { setFeatureError(err.message); }
  };
  const handleFeatureEdit = f => {
    setFeatureForm({
      project_id: f.project_id || '',
      fr_id: f.fr_id || '',
      name: f.name,
      feature: f.feature || '',
      description: f.description || ''
    });
    setEditingFeatureId(f.id);
    setFeatureDialogOpen(true);
    setFeatureDialogFor('feature');
  };
  const handleFeatureDelete = async id => {
    if (!window.confirm('Delete this feature?')) return;
    setFeatureError(''); setFeatureSuccess('');
    try {
      const res = await fetch(`/api/features/${id}`, { method: 'DELETE' });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to delete feature');
      setFeatureSuccess('Feature deleted!');
      fetchFeatures();
    } catch (err) { setFeatureError(err.message); }
  };

  // Task form handlers
  const handleTaskChange = e => setTaskForm(f => ({ ...f, [e.target.name]: e.target.value }));
  const handleTaskSubmit = async e => {
    e.preventDefault();
    setTaskError(''); setTaskSuccess('');
    try {
      const res = await fetch(editingTaskId ? `/api/tasks/${editingTaskId}` : '/api/tasks', {
        method: editingTaskId ? 'PUT' : 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(taskForm)
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to save task');
      setTaskSuccess(editingTaskId ? 'Task updated!' : 'Task added!');
      setTaskForm(emptyTask);
      setEditingTaskId(null);
      setTaskDialogOpen(false);
      fetchTasks();
    } catch (err) { setTaskError(err.message); }
  };
  const handleTaskEdit = t => {
    setTaskForm({ project_id: t.project_id || '', feature_id: t.feature_id || '', title: t.title, description: t.description || '', status: t.status || '', assignee: t.assignee || '' });
    setEditingTaskId(t.id);
    setTaskDialogOpen(true);
  };
  const handleTaskDelete = async id => {
    if (!window.confirm('Delete this task?')) return;
    setTaskError(''); setTaskSuccess('');
    try {
      const res = await fetch(`/api/tasks/${id}`, { method: 'DELETE' });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to delete task');
      setTaskSuccess('Task deleted!');
      fetchTasks();
    } catch (err) { setTaskError(err.message); }
  };

  // Project form handlers (for inline creation)
  const handleProjectChange = e => setProjectForm(f => ({ ...f, [e.target.name]: e.target.value }));
  const handleProjectSubmit = async e => {
    e.preventDefault();
    setProjectError(''); setProjectSuccess('');
    try {
      const res = await fetch('/api/projects', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(projectForm)
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to create project');
      setProjectSuccess('Project created!');
      setProjectForm(emptyProject);
      setProjectDialogOpen(false);
      fetchProjects();
      // Set the new project as selected in the parent form
      if (featureDialogFor === 'feature') {
        setFeatureForm(f => ({ ...f, project_id: data.id }));
      } else {
        setTaskForm(f => ({ ...f, project_id: data.id }));
      }
    } catch (err) { setProjectError(err.message); }
  };

  // Feature form for Add Task dialog (inline creation)
  const [featureInlineForm, setFeatureInlineForm] = useState(emptyFeature);
  const [featureInlineDialogOpen, setFeatureInlineDialogOpen] = useState(false);
  const [featureInlineError, setFeatureInlineError] = useState('');
  const [featureInlineSuccess, setFeatureInlineSuccess] = useState('');
  const handleFeatureInlineChange = e => setFeatureInlineForm(f => ({ ...f, [e.target.name]: e.target.value }));
  const handleFeatureInlineSubmit = async e => {
    e.preventDefault();
    setFeatureInlineError(''); setFeatureInlineSuccess('');
    try {
      const res = await fetch('/api/features', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...featureInlineForm, project_id: taskForm.project_id })
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to create feature');
      setFeatureInlineSuccess('Feature created!');
      setFeatureInlineForm(emptyFeature);
      setFeatureInlineDialogOpen(false);
      fetchFeatures();
      setTaskForm(f => ({ ...f, feature_id: data.id }));
    } catch (err) { setFeatureInlineError(err.message); }
  };

  return (
    <Box>
      {/* Project Selector and Initial Project List */}
      {!selectedProjectId ? (
        <Box
          sx={{
            minHeight: '70vh',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            bgcolor: 'background.default',
            p: 4,
          }}
        >
          <Box sx={{ width: '100%', maxWidth: 900, mb: 3, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Typography variant="h4" sx={{ fontWeight: 600, color: 'primary.main' }}>
              Select a Project to View Features & Tasks
            </Typography>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => { setProjectDialogOpen(true); setFeatureDialogFor('feature'); }}
            >
              Add New Project
            </Button>
          </Box>
          <TextField
            label="Search Projects"
            variant="outlined"
            size="medium"
            value={projectSearch}
            onChange={e => setProjectSearch(e.target.value)}
            sx={{ mb: 3, width: 350 }}
          />
          <Grid container spacing={3} justifyContent="center" sx={{ maxWidth: 900 }}>
            {filteredProjects.length === 0 ? (
              <Grid item xs={12}>
                <Typography color="text.secondary">No projects found.</Typography>
              </Grid>
            ) : (
              filteredProjects.map((p) => (
                <Grid item xs={12} sm={6} md={4} key={p.id}>
                  <Card
                    sx={{
                      cursor: 'pointer',
                      transition: 'box-shadow 0.2s, transform 0.2s',
                      boxShadow: 2,
                      '&:hover': {
                        boxShadow: 8,
                        transform: 'translateY(-4px) scale(1.03)',
                        bgcolor: 'primary.light',
                      },
                      minHeight: 180,
                      height: 180,
                      width: '100%',
                      maxWidth: 320,
                      mx: 'auto',
                      display: 'flex',
                      flexDirection: 'column',
                      justifyContent: 'center',
                    }}
                    onClick={() => setSelectedProjectId(p.id)}
                  >
                    <CardContent
                      sx={{
                        flex: 1,
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'center',
                        alignItems: 'center',
                        height: '100%',
                        p: 2,
                        width: '100%',
                      }}
                    >
                      <Typography variant="h6" sx={{ color: 'primary.dark', fontWeight: 500, textAlign: 'center' }}>
                        {p.name}
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ mt: 1, textAlign: 'center' }}>
                        {p.description}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
              ))
            )}
          </Grid>
        </Box>
      ) : (
        <>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <Button
              variant="outlined"
              startIcon={<ArrowBackIcon />}
              onClick={() => setSelectedProjectId("")}
              sx={{ mr: 2 }}
            >
              Go Back
            </Button>
            <Typography variant="h4">Features & Tasks</Typography>
          </Box>
          {/* Project Selector */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
            <TextField
              label="Project"
              select
              value={selectedProjectId}
              onChange={e => setSelectedProjectId(e.target.value)}
              size="small"
              sx={{ minWidth: 220 }}
            >
              {projects.map(p => (
                <MenuItem key={p.id} value={p.id}>{p.name}</MenuItem>
              ))}
            </TextField>
            <Button variant="contained" startIcon={<AddIcon />} onClick={() => { setProjectDialogOpen(true); setFeatureDialogFor('feature'); }}>Add Project</Button>
          </Box>
          {/* Feature Listing Table */}
          <Paper sx={{ p: 2, mb: 3 }}>
            <Typography variant="h6" sx={{ mb: 2 }}>Feature Listing</Typography>
            <TextField
              label="Search Features"
              variant="outlined"
              size="small"
              value={featureSearch}
              onChange={e => setFeatureSearch(e.target.value)}
              sx={{ mb: 2, width: 350 }}
            />
            {/* New Feature Rows Section */}
            {newFeatures.length > 0 && (
              <NewFeaturesDnDTable
                newFeatures={newFeatures}
                onDragEnd={handleNewFeaturesDragEnd}
                onChange={handleNewFeatureChange}
                onRemove={handleRemoveNewFeatureRow}
                onSave={handleSaveAllNewFeatures}
              />
            )}
            <Button variant="outlined" onClick={handleAddNewFeatureRow} sx={{ mb: 2 }}>Add New Feature Row</Button>
            {/* Drag-and-drop for saved features */}
            <SavedFeaturesDnDTable
              reorderedFeatures={filteredFeaturesWithFrId}
              onDragEnd={handleSavedFeaturesDragEnd}
              onEdit={handleFeatureEdit}
              onDelete={handleFeatureDelete}
            />
            {isReordering && (
              <Button variant="contained" color="primary" onClick={handleSaveFeatureOrder} sx={{ mt: 2 }}>
                Save Order
              </Button>
            )}
          </Paper>
        </>
      )}
      {/* Add/Edit Feature Dialog */}
      <Dialog open={featureDialogOpen} onClose={() => setFeatureDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{editingFeatureId ? 'Edit Feature' : 'Add Feature'}</DialogTitle>
        <DialogContent>
          <Box component="form" onSubmit={handleFeatureSubmit} sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <TextField label="FR_ID" name="fr_id" value={featureForm.fr_id} onChange={handleFeatureChange} size="small" required helperText="Auto-generated, but you can edit. Must be unique per project." />
            <TextField label="Module" name="name" value={featureForm.name} onChange={handleFeatureChange} size="small" required />
            <TextField label="Feature" name="feature" value={featureForm.feature} onChange={handleFeatureChange} size="small" required />
            <TextField label="Feature" name="description" value={featureForm.description} onChange={handleFeatureChange} size="small" />
            <input type="hidden" name="project_id" value={selectedProjectId} />
            <Button type="submit" variant="contained">{editingFeatureId ? 'Update' : 'Add'} Feature</Button>
            {editingFeatureId && <Button onClick={() => { setFeatureForm(emptyFeature); setEditingFeatureId(null); setFeatureDialogOpen(false); }} color="secondary">Cancel</Button>}
          </Box>
        </DialogContent>
      </Dialog>
      {/* Add/Edit Task Dialog */}
      <Dialog open={taskDialogOpen} onClose={() => setTaskDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{editingTaskId ? 'Edit Task' : 'Add Task'}</DialogTitle>
        <DialogContent>
          <Box component="form" onSubmit={handleTaskSubmit} sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <TextField label="Project" name="project_id" value={taskForm.project_id} onChange={handleTaskChange} size="small" select required sx={{ flex: 1 }}>
                {projects.map(p => <MenuItem key={p.id} value={p.id}>{p.name}</MenuItem>)}
              </TextField>
              <IconButton onClick={() => { setProjectDialogOpen(true); setFeatureDialogFor('task'); }} size="small"><AddIcon /></IconButton>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <TextField label="Feature" name="feature_id" value={taskForm.feature_id} onChange={handleTaskChange} size="small" select required sx={{ flex: 1 }}>
                {features.filter(f => f.project_id === taskForm.project_id).map(f => <MenuItem key={f.id} value={f.id}>{f.name}</MenuItem>)}
              </TextField>
              <IconButton onClick={() => setFeatureInlineDialogOpen(true)} size="small"><AddIcon /></IconButton>
            </Box>
            <TextField label="Title" name="title" value={taskForm.title} onChange={handleTaskChange} size="small" required />
            <TextField label="Description" name="description" value={taskForm.description} onChange={handleTaskChange} size="small" />
            <TextField label="Status" name="status" value={taskForm.status} onChange={handleTaskChange} size="small" select>
              {TASK_STATUSES.filter(Boolean).map(s => <MenuItem key={s} value={s}>{s}</MenuItem>)}
            </TextField>
            <TextField label="Assignee" name="assignee" value={taskForm.assignee} onChange={handleTaskChange} size="small" />
            <Button type="submit" variant="contained">{editingTaskId ? 'Update' : 'Add'} Task</Button>
            {editingTaskId && <Button onClick={() => { setTaskForm(emptyTask); setEditingTaskId(null); setTaskDialogOpen(false); }} color="secondary">Cancel</Button>}
          </Box>
        </DialogContent>
      </Dialog>
      {/* Inline Project Dialog (for both Add Feature and Add Task) */}
      <Dialog open={projectDialogOpen} onClose={() => setProjectDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Add Project</DialogTitle>
        <DialogContent>
          <Box component="form" onSubmit={handleProjectSubmit} sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <TextField label="Name" name="name" value={projectForm.name} onChange={handleProjectChange} size="small" required />
            <TextField label="Description" name="description" value={projectForm.description} onChange={handleProjectChange} size="small" />
            <Button type="submit" variant="contained">Add Project</Button>
            <Button onClick={() => setProjectDialogOpen(false)} color="secondary">Cancel</Button>
          </Box>
        </DialogContent>
      </Dialog>
      {/* Inline Feature Dialog (for Add Task) */}
      <Dialog open={featureInlineDialogOpen} onClose={() => setFeatureInlineDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Add Feature</DialogTitle>
        <DialogContent>
          <Box component="form" onSubmit={handleFeatureInlineSubmit} sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <TextField label="Name" name="name" value={featureInlineForm.name} onChange={handleFeatureInlineChange} size="small" required />
            <TextField label="Description" name="description" value={featureInlineForm.description} onChange={handleFeatureInlineChange} size="small" />
            <Button type="submit" variant="contained">Add Feature</Button>
            <Button onClick={() => setFeatureInlineDialogOpen(false)} color="secondary">Cancel</Button>
          </Box>
        </DialogContent>
      </Dialog>
      {/* Snackbars */}
      <Snackbar open={!!featureSuccess} autoHideDuration={3000} onClose={() => setFeatureSuccess('')} anchorOrigin={{ vertical: 'top', horizontal: 'center' }}>
        <Alert onClose={() => setFeatureSuccess('')} severity="success">{featureSuccess}</Alert>
      </Snackbar>
      <Snackbar open={!!featureError} autoHideDuration={4000} onClose={() => setFeatureError('')} anchorOrigin={{ vertical: 'top', horizontal: 'center' }}>
        <Alert onClose={() => setFeatureError('')} severity="error">{featureError}</Alert>
      </Snackbar>
      <Snackbar open={!!taskSuccess} autoHideDuration={3000} onClose={() => setTaskSuccess('')} anchorOrigin={{ vertical: 'top', horizontal: 'center' }}>
        <Alert onClose={() => setTaskSuccess('')} severity="success">{taskSuccess}</Alert>
      </Snackbar>
      <Snackbar open={!!taskError} autoHideDuration={4000} onClose={() => setTaskError('')} anchorOrigin={{ vertical: 'top', horizontal: 'center' }}>
        <Alert onClose={() => setTaskError('')} severity="error">{taskError}</Alert>
      </Snackbar>
      <Snackbar open={!!projectSuccess} autoHideDuration={3000} onClose={() => setProjectSuccess('')} anchorOrigin={{ vertical: 'top', horizontal: 'center' }}>
        <Alert onClose={() => setProjectSuccess('')} severity="success">{projectSuccess}</Alert>
      </Snackbar>
      <Snackbar open={!!projectError} autoHideDuration={4000} onClose={() => setProjectError('')} anchorOrigin={{ vertical: 'top', horizontal: 'center' }}>
        <Alert onClose={() => setProjectError('')} severity="error">{projectError}</Alert>
      </Snackbar>
      <Snackbar open={!!featureInlineSuccess} autoHideDuration={3000} onClose={() => setFeatureInlineSuccess('')} anchorOrigin={{ vertical: 'top', horizontal: 'center' }}>
        <Alert onClose={() => setFeatureInlineSuccess('')} severity="success">{featureInlineSuccess}</Alert>
      </Snackbar>
      <Snackbar open={!!featureInlineError} autoHideDuration={4000} onClose={() => setFeatureInlineError('')} anchorOrigin={{ vertical: 'top', horizontal: 'center' }}>
        <Alert onClose={() => setFeatureInlineError('')} severity="error">{featureInlineError}</Alert>
      </Snackbar>
    </Box>
  );
};

export default FeatureTaskPage; 